import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Zap, Layout, ArrowRight, Shield, Globe, Paperclip, CheckCircle, User, Menu, X, Home, Sparkles, Info, ChevronDown } from 'lucide-react';
import { useAuth, SignInButton } from '@clerk/clerk-react';
import { AdBanner } from './ads';
import ThemeToggle from './ThemeToggle';

const LandingPage: React.FC = () => {
  const { isSignedIn: user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const navItems = [
    { name: 'Home', href: '#', icon: Home },
    { name: 'Features', href: '#features', icon: Sparkles },
    { name: 'How It Works', href: '#how-it-works', icon: Layout },
    { name: 'Security', href: '#security', icon: Shield },
  ];

  const faqs = [
    {
      q: 'What is LuckySend?',
      a: 'LuckySend is a free web-based email tool that lets you compose and send rich HTML emails with file attachments directly from your Gmail account. It uses Google\'s official OAuth 2.0 API — no passwords stored, no third-party relay.'
    },
    {
      q: 'Is LuckySend free to use?',
      a: 'Yes, completely free. LuckySend is supported by Google AdSense advertising displayed on public pages. The core email-sending feature is and will remain free forever.'
    },
    {
      q: 'Does LuckySend store my Gmail password?',
      a: 'Never. LuckySend uses Google OAuth 2.0, which means you authenticate directly with Google and we receive only a temporary access token. Your password is never shared with us. You can revoke access at any time from your Google account settings.'
    },
    {
      q: 'What types of files can I attach to my emails?',
      a: 'You can attach any file type — PDFs, images (JPG, PNG, GIF), Word documents, Excel spreadsheets, ZIP archives, and more — subject to Gmail\'s standard attachment size limits (25 MB per email). LuckySend automatically validates file sizes before sending.'
    },
    {
      q: 'Can I send HTML email templates?',
      a: 'Absolutely. LuckySend\'s composer supports full HTML input. You can paste any custom HTML template or use our AI-assisted content engine to generate a professional HTML layout from a simple text description. Saved templates can be reused across sessions.'
    },
    {
      q: 'How is LuckySend different from Gmail\'s built-in compose?',
      a: 'Gmail\'s built-in compose has limited HTML support and no easy way to paste raw HTML or bulk-attach files programmatically. LuckySend gives you a dedicated HTML editor, AI content generation, template management, and a streamlined send workflow — all without leaving your browser.'
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0b] transition-colors duration-500 selection:bg-blue-500/30 selection:text-blue-900 dark:selection:text-blue-100">
      {/* Texture Overlay */}
      <div className="fixed inset-0 pointer-events-none z-[100] opacity-[0.03] dark:opacity-[0.05]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>

      {/* Header Navigation */}
      <header className="fixed top-0 inset-x-0 h-20 bg-white/70 dark:bg-[#0a0a0b]/70 backdrop-blur-xl border-b border-gray-100 dark:border-white/5 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto h-full px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="p-2.5 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-xl group-hover:rotate-6 transition-transform duration-300 shadow-xl shadow-blue-500/20">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-2xl tracking-tight text-gray-900 dark:text-white">
              Lucky<span className="text-blue-600">Send</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-10">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm font-bold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                {item.name}
              </a>
            ))}
            <div className="h-4 w-px bg-gray-200 dark:bg-white/10 mx-2" />

            <div className="flex items-center space-x-4">
              <ThemeToggle />
              {user ? (
                <Link
                  to="/app"
                  className="bg-gray-900 dark:bg-white text-white dark:text-black px-6 py-2.5 rounded-xl font-bold text-sm hover:scale-105 transition-all shadow-xl active:scale-95"
                >
                  Dashboard
                </Link>
              ) : (
                <SignInButton mode="modal">
                  <button className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/30 transition-all active:scale-95">
                    Start Sending
                  </button>
                </SignInButton>
              )}
            </div>
          </nav>

          {/* Mobile Menu Toggle & Toggle */}
          <div className="flex items-center space-x-4 lg:hidden">
            <ThemeToggle />
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-colors"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </header>

      {/* Sidenav Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-950/60 backdrop-blur-md z-[60] lg:hidden animate-in fade-in duration-500"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Refined Sidenav (Right Side) */}
      <aside
        className={`fixed top-0 bottom-0 right-0 w-[300px] bg-white dark:bg-[#0f0f11] z-[70] shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] lg:hidden ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-8 flex items-center justify-between border-b border-gray-50 dark:border-white/5">
            <span className="font-bold text-xl dark:text-white tracking-tight text-gray-900">Navigation</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <nav className="flex-1 p-8 space-y-6">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setSidebarOpen(false)}
                className="flex items-center space-x-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-white transition-all group"
              >
                <item.icon size={20} className="group-hover:rotate-12 transition-transform" />
                <span className="font-bold text-lg">{item.name}</span>
              </a>
            ))}
          </nav>

          <div className="p-8 space-y-4">
            {user ? (
              <Link
                to="/app"
                className="flex items-center justify-center space-x-2 w-full bg-gray-900 dark:bg-white text-white dark:text-black px-6 py-4 rounded-2xl font-bold shadow-xl active:scale-95 transition-all"
              >
                <User size={20} />
                <span>Open App</span>
              </Link>
            ) : (
              <SignInButton mode="modal">
                <button className="flex items-center justify-center space-x-2 w-full bg-blue-600 text-white px-6 py-4 rounded-2xl font-bold shadow-lg shadow-blue-500/20 active:scale-95 transition-all">
                  <User size={20} />
                  <span>Get Started</span>
                </button>
              </SignInButton>
            )}
          </div>
        </div>
      </aside>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[800px] h-[800px] bg-blue-500/10 dark:bg-blue-600/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/10 dark:bg-indigo-600/5 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 mb-6 animate-in fade-in slide-in-from-top-4 duration-1000">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-widest">Now Powered by Google OAuth</span>
              </div>

              <h1 className="text-4xl lg:text-7xl font-black text-gray-900 dark:text-white mb-8 leading-[1.1] tracking-tight">
                Send <span className="text-blue-600">better</span> emails, <br className="hidden md:block" />
                without the friction.
              </h1>

              <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-xl mx-auto lg:ml-0 leading-relaxed font-medium">
                The most direct way to blast HTML emails with attachments. No complex setup—just connect, compose, and deploy.
              </p>

              <div className="flex flex-col sm:flex-row justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-5">
                {user ? (
                  <Link
                    to="/app"
                    className="bg-blue-600 text-white px-8 py-4 rounded-2xl hover:bg-blue-700 transition-all font-bold text-lg inline-flex items-center justify-center shadow-2xl shadow-blue-500/25 active:scale-95"
                  >
                    Go to Console
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                ) : (
                  <SignInButton mode="modal">
                    <button className="bg-blue-600 text-white px-8 py-4 rounded-2xl hover:bg-blue-700 transition-all font-bold text-lg inline-flex items-center justify-center shadow-2xl shadow-blue-500/25 active:scale-95">
                      Get Early Access
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </button>
                  </SignInButton>
                )}
                <a
                  href="#features"
                  className="px-8 py-4 rounded-2xl border-2 border-gray-100 dark:border-white/5 text-gray-900 dark:text-white font-bold text-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-center"
                >
                  Features
                </a>
              </div>

              {/* Social Proof */}
              <div className="mt-12 flex items-center justify-center lg:justify-start space-x-4 grayscale opacity-50 dark:invert">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Verified Connection</div>
                <div className="h-4 w-px bg-gray-300"></div>
                <Mail className="w-5 h-5 text-gray-400" />
                <Globe className="w-5 h-5 text-gray-400" />
                <Shield className="w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Visual element - Mock Preview */}
            <div className="relative group lg:block hidden">
              <div className="absolute -inset-1 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-[2.5rem] blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative bg-white dark:bg-[#121214] border border-gray-100 dark:border-white/5 rounded-[2.5rem] p-8 shadow-2xl">
                <div className="flex items-center space-x-2 mb-8">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                </div>
                <div className="space-y-4">
                  <div className="h-6 bg-gray-100 dark:bg-white/5 rounded-lg w-3/4"></div>
                  <div className="h-40 bg-gray-50 dark:bg-white/10 rounded-2xl w-full"></div>
                  <div className="flex space-x-3">
                    <div className="h-10 bg-blue-600 rounded-xl w-32"></div>
                    <div className="h-10 border border-gray-200 dark:border-white/10 rounded-xl w-24"></div>
                  </div>
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -right-6 bg-white dark:bg-[#1a1a1c] p-4 rounded-2xl shadow-2xl border border-gray-50 dark:border-white/5 animate-bounce">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs font-black dark:text-white uppercase tracking-tighter">Email Deployed</p>
                      <p className="text-[10px] text-gray-400 font-bold">via Gmail API</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Features Section */}
      <section id="features" className="py-24 bg-white dark:bg-[#0a0a0b]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-20">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-6 tracking-tight">Focus on content. <br />We'll handle the <span className="text-blue-600">rest</span>.</h2>
            <p className="text-xl text-gray-500 dark:text-gray-400 font-medium max-w-2xl">Our toolset is designed for speed, security, and professional results.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 auto-rows-[250px] md:auto-rows-[200px] gap-4">
            {/* Feature 1 - Large */}
            <div className="md:col-span-3 lg:col-span-8 md:row-span-2 bg-[#f8f9fb] dark:bg-[#121214] rounded-[2.5rem] p-8 lg:p-12 relative overflow-hidden group border border-gray-100 dark:border-white/5">
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="w-14 h-14 bg-white dark:bg-white/10 rounded-2xl flex items-center justify-center shadow-lg mb-6">
                    <Zap className="text-blue-600 w-7 h-7" />
                  </div>
                  <h3 className="text-3xl font-black text-gray-900 dark:text-white mb-4">AI Content Engine</h3>
                  <p className="text-gray-500 dark:text-gray-400 font-medium text-lg max-w-md">Transform rough ideas into production-ready HTML in seconds. Use localized tweaking to perfect your tone without touching a single line of code.</p>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2 group-hover:scale-150 transition-transform duration-1000"></div>
            </div>

            {/* Feature 2 - Square */}
            <div className="md:col-span-3 lg:col-span-4 md:row-span-2 bg-[#f8f9fb] dark:bg-[#121214] rounded-[2.5rem] p-8 relative overflow-hidden group border border-gray-100 dark:border-white/5">
              <div className="w-14 h-14 bg-white dark:bg-white/10 rounded-2xl flex items-center justify-center shadow-lg mb-6">
                <Shield className="text-blue-600 w-7 h-7" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4 whitespace-nowrap">Secure OAuth 2.0</h3>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Direct connection via Google's official API. Your account security is never compromised.</p>
              <div className="absolute bottom-0 right-0 p-8 opacity-10 group-hover:scale-110 group-hover:opacity-20 transition-all duration-500">
                <Shield size={120} className="text-blue-600" />
              </div>
            </div>

            {/* Feature 3 - Wide */}
            <div className="md:col-span-4 lg:col-span-4 bg-[#f8f9fb] dark:bg-[#121214] rounded-[2.5rem] p-8 relative overflow-hidden border border-gray-100 dark:border-white/5">
              <div className="flex items-center space-x-5 mb-4">
                <div className="w-12 h-12 bg-white dark:bg-white/10 rounded-xl flex items-center justify-center shadow-md">
                  <Paperclip className="text-blue-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white">Smart Files</h3>
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Bulk attachments with automatic size validation and compression support.</p>
            </div>

            {/* Feature 4 - Wide */}
            <div className="md:col-span-2 lg:col-span-4 bg-[#f8f9fb] dark:bg-[#121214] rounded-[2.5rem] p-8 relative overflow-hidden border border-gray-100 dark:border-white/5">
              <div className="flex items-center space-x-5 mb-4">
                <div className="w-12 h-12 bg-white dark:bg-white/10 rounded-xl flex items-center justify-center shadow-md">
                  <Layout className="text-blue-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white">Templates</h3>
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Save recurring drafts and reuse them globally.</p>
            </div>

            {/* Feature 5 - Wide */}
            <div className="md:col-span-6 lg:col-span-4 bg-gray-950 rounded-[2.5rem] p-8 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-black text-white mb-2 tracking-tight group-hover:translate-x-1 transition-transform">Ready for dark mode.</h3>
                <p className="text-gray-400 font-medium text-sm">Adaptive UI that's easy on the eyes, day or night.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-gray-50 dark:bg-[#0f0f11] border-y border-gray-100 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">Simplified Workflow</h2>
            <p className="text-gray-500 dark:text-gray-400 font-medium">From zero to sent in less than 60 seconds.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
            {[
              { step: '01', title: 'Authenticate', desc: 'Securely link your Google workspace via official OAuth 2.0 login.' },
              { step: '02', title: 'Compose', desc: 'Draft your message or let AI generate a professional HTML layout.' },
              { step: '03', title: 'Deploy', desc: 'Review, attach files, and hit send. Real-time delivery status.' }
            ].map((item, i) => (
              <div key={i} className="relative group">
                <div className="text-6xl font-black text-gray-100 dark:text-white/5 absolute -top-8 -left-4 transition-colors group-hover:text-blue-500/10">
                  {item.step}
                </div>
                <div className="relative">
                  <h3 className="text-xl font-black text-gray-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-24 bg-white dark:bg-[#0a0a0b]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-[2rem] flex items-center justify-center mx-auto mb-10 border border-blue-100 dark:border-blue-800">
            <Shield className="w-10 h-10 text-blue-600" />
          </div>
          <h2 className="text-3xl lg:text-5xl font-black text-gray-900 dark:text-white mb-8 tracking-tight">Your data belongs to you.</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 font-medium leading-relaxed">
            Lucky Send uses temporary access tokens provided by Google. We never see, touch, or store your passwords. Everything is built on standard OAuth 2.0 protocols.
          </p>
          <div className="inline-flex items-center space-x-2 text-blue-600 font-black uppercase text-xs tracking-widest">
            <Globe className="w-4 h-4" />
            <span>Enterprise Protocol Compliance</span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff10_1px,transparent_1px),linear-gradient(to_bottom,#ffffff10_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-8">Ready to send better?</h2>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            {user ? (
              <Link to="/app" className="bg-white text-blue-600 px-10 py-5 rounded-2xl font-black shadow-2xl hover:scale-105 active:scale-95 transition-all">Go to App</Link>
            ) : (
              <SignInButton mode="modal">
                <button className="bg-white text-blue-600 px-10 py-5 rounded-2xl font-black shadow-2xl hover:scale-105 active:scale-95 transition-all">Start Now</button>
              </SignInButton>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gray-50 dark:bg-[#0f0f11] border-t border-gray-100 dark:border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Everything you need to know about LuckySend.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white dark:bg-[#121214] border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="font-bold text-gray-900 dark:text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Blurb */}
      <section className="py-16 bg-white dark:bg-[#0a0a0b] border-t border-gray-100 dark:border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">About LuckySend</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto mb-6">
            LuckySend is a free email productivity tool built by{' '}
            <a href="https://forouverse.solutions" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">Forouverse Solutions</a>.
            Our mission is to make sending professional HTML emails as simple as possible — without compromising your security or privacy.
            We use Google OAuth 2.0 so your credentials are always safe.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center space-x-2 text-blue-600 font-bold hover:underline"
          >
            <span>Learn more about us</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Ads */}
      <div className="bg-gray-50 dark:bg-[#0f0f11] py-12">
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          <AdBanner slot={process.env.REACT_APP_ADSENSE_HEADER_SLOT || "1234567890"} format="horizontal" />
        </div>
      </div>

      <footer className="bg-white dark:bg-[#0a0a0b] border-t border-gray-100 dark:border-white/5 py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center group">
          <div className="flex items-center space-x-3 mb-8 opacity-50 group-hover:opacity-100 transition-opacity">
            <div className="p-1.5 bg-gray-900 dark:bg-white rounded-lg">
              <Mail className="w-4 h-4 text-white dark:text-black" />
            </div>
            <span className="font-bold text-gray-900 dark:text-white tracking-tighter">LuckySend</span>
          </div>

          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <Link to="/about" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">About</Link>
            <Link to="/terms" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Terms</Link>
            <Link to="/privacy" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Privacy</Link>
            <a href="mailto:support@luckysend.app" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Contact</a>
            <a href="https://forouverse.solutions" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Forouverse Solutions</a>
          </div>

          <p className="text-xs font-medium text-gray-400">© 2026 LuckySend Application. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
